import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import './AddProduct.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import Select from 'react-select';
import { VIEW_PRODUCT_API, UPDATE_PRODUCT_API, GET_ALL_PRODUCT_CATEGORIES_API, GET_ALL_UNITS_API } from '../config/API';
import Loader from '../components/common/Loader';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { showErrorToast } from '../utils/toastUtils';

const EditProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we are in 'view' mode from path or state
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [formData, setFormData] = useState({
        productName: '',
        description: '',
        defaultRate: '',
        categoryId: '',
        unitId: ''
    });
    // Options are handled by PaginatedSelect

    // Category and Unit options are now handled by PaginatedSelect

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');

                // Fetch from API
                const response = await axios.get(VIEW_PRODUCT_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const p = response.data.data;
                    setFormData({
                        productName: p.productName || '',
                        description: p.description || '',
                        defaultRate: p.defaultRate || '',
                        categoryId: p.ProductCategory?._id || p.ProductCategory?.id || p.categoryId || '',
                        categoryName: p.ProductCategory?.categoryName || '',
                        unitId: p.Unit?._id || p.Unit?.id || p.unitId || '',
                        unitName: p.Unit?.unitName || ''
                    });
                }
            } catch (error) {
                console.error('Error fetching product data:', error);
            } finally {
                setFetchingData(false);
            }
        };

        fetchProductData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCategoryChange = (selectedOption) => {
        const val = selectedOption ? selectedOption.value : '';
        const unitVal = selectedOption ? selectedOption.unitId : '';
        setFormData(prev => ({
            ...prev,
            categoryId: val,
            categoryName: selectedOption ? selectedOption.label : '',
            unitId: unitVal || prev.unitId,
            unitName: selectedOption ? (selectedOption.unitName || prev.unitName) : prev.unitName
        }));
    };

    const handleUnitChange = (selectedOption) => {
        const val = selectedOption ? selectedOption.value : '';
        setFormData(prev => ({
            ...prev,
            unitId: val,
            unitName: selectedOption ? selectedOption.label : ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewMode) return;

        const result = await Swal.fire({
            title: 'Update Product?',
            text: 'Do you want to save changes to this product?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const payload = {
                    productName: formData.productName,
                    description: formData.description,
                    defaultRate: formData.defaultRate,
                    productCategoryId: formData.categoryId,
                    unitId: formData.unitId
                };
                const response = await axios.put(UPDATE_PRODUCT_API(id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Product updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/products');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update product');
                }
            } catch (error) {
                console.error('Error updating product:', error);
                const errorData = error.response?.data?.errors || error.response?.data?.message || error.response?.data?.error || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Updating product..." />;
    }

    return (
        <div className="add-product-page">
            <div className="page-header">
                <button onClick={() => navigate('/products')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Product Details' : 'Edit Product'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h3>Product Information</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Product Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="productName"
                                value={formData.productName}
                                onChange={handleChange}
                                placeholder="Enter product name"
                                disabled={isViewMode}
                            />
                        </div>

                        <div className="form-group">
                            <label>Category <span className="required-star">*</span></label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_PRODUCT_CATEGORIES_API}
                                mapResponse={(cat) => ({
                                    value: cat._id || cat.id,
                                    label: cat.categoryName,
                                    unitId: cat.unitId || cat.Unit?._id || cat.Unit?.id || ''
                                })}
                                value={formData.categoryId ? { value: formData.categoryId, label: formData.categoryName || 'Selected Category' } : null}
                                onChange={handleCategoryChange}
                                placeholder="Select category"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isDisabled={isViewMode}
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label>Unit <span className="required-star">*</span></label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_UNITS_API}
                                mapResponse={(u) => ({
                                    value: u._id || u.id,
                                    label: u.unitName
                                })}
                                value={formData.unitId ? { value: formData.unitId, label: formData.unitName || 'Selected Unit' } : null}
                                onChange={handleUnitChange}
                                placeholder="Select Unit"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isDisabled={isViewMode}
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label>Default Rate (₹) </label>
                            <input
                                type="number"
                                name="defaultRate"
                                value={formData.defaultRate}
                                onChange={handleChange}
                                placeholder="Enter amount"
                                min="0"
                                disabled={isViewMode}
                            />
                        </div>

                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Enter product description"
                                disabled={isViewMode}
                            ></textarea>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/products')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Product
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditProduct;
